import React, { createContext, useReducer, useContext, useMemo } from 'react';
import { getFileExtension, getExtension, getParsedCode } from 'app/components/ImageUploader/uploader-v2/utils';

export const ImgContext = createContext(null);
export const ImgDispatchContext = createContext(null);

const ORIGIN_IDX = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const UPDATE_IDX = ['015', '016', '017', '018', '019', '020', '021', '022', '023', '024'];
const STATIC_IMG_LENGTH = 10;

const initialImgs = [
	{
		id: 1,
		text: '앞측면',
		type: 'sample_front_side',
		photoType: 1,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		// originBlogUrl - URL.createObjectURL(curr);
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 2,
		text: '뒷측면',
		type: 'sample_rear_side',
		photoType: 2,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 3,
		text: '전면',
		type: 'sample_front',
		photoType: 3,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 4,
		text: '후면',
		type: 'sample_rear',
		photoType: 4,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 5,
		text: '휠/타이어',
		type: 'sample_wheel',
		photoType: 5,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 6,
		text: '엔진',
		type: 'sample_engine',
		photoType: 6,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 7,
		text: '실내',
		type: 'sample_interior',
		photoType: 7,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 8,
		text: '계기판',
		type: 'sample_dashboard',
		photoType: 8,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 9,
		text: '변속기',
		type: 'sample_gear',
		photoType: 9,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
	{
		id: 10,
		text: '차량시트',
		type: 'sample_seat',
		photoType: 10,
		src: null,
		fileExtension: '',
		description: '',
		timestamp: '',
		originUrl: '',
		recentBlobUrl: '',
		isValidRatio: true,
		isResized: false,
		isModified: false,
	},
];

function imgReducer(imgs, action) {
	switch (action.type) {
		case 'SET_API_IMG': {
			const { src, isValidRatio, photo } = action;

			if (ORIGIN_IDX.includes(photo.photoType)) {
				return imgs.map((item) => {
					if (item.photoType === photo.photoType) {
						return {
							...item,
							src,
							fileExtension: getFileExtension(photo.photoPath),
							originUrl: photo.photoPath,
							timestamp: photo.uptDate,
							description: photo.photoDescription ?? '',
							isResized: true,
							isValidRatio,
						};
					}
					return item;
				});
			}

			return [
				...imgs,
				{
					...initialImgs[0],
					src,
					text: '옵션',
					type: 'sample_extra',
					id: imgs.length + 1,
					photoType: getParsedCode(UPDATE_IDX[imgs.length - STATIC_IMG_LENGTH]),
					originUrl: photo.photoPath,
					timestamp: photo.uptDate,
					description: photo.photoDescription ?? '',
					fileExtension: getFileExtension(photo.photoPath),
					isResized: true,
					isValidRatio,
				},
			];
		}

		case 'SET_IMG': {
			const { src, isValidRatio, isResized = true } = action;
			return imgs.map((item) => {
				if (item.id === action.id) {
					return {
						...item,
						isValidRatio,
						isResized,
						src,
					};
				}
				return item;
			});
		}

		case 'DELETE_IMG': {
			if (action.id > STATIC_IMG_LENGTH) {
				return imgs.filter((item) => item.id !== action.id);
			}
			return imgs.map((item) => {
				if (item.id === action.id) {
					return initialImgs[item.id - 1];
				}
				return item;
			});
		}

		case 'ADD_IMG': {
			// 기존 매핑시켜준 타입, 썸네일 타입 제외
			const { photo, src } = action;
			const requiredImgIdx = imgs.findIndex((img) => img.src === null);
			const url = URL.createObjectURL(photo);

			if (requiredImgIdx === -1) {
				return [
					...imgs,
					{
						id: imgs.length + 1,
						photoType: UPDATE_IDX[imgs.length - STATIC_IMG_LENGTH],
						text: '옵션',
						type: 'sample_extra',
						src,
						fileExtension: getExtension(photo.name)[0],
						description: null,
						timestamp: '',
						originUrl: url,
						recentBlobUrl: '',
						isValidRatio: true,
						isResized: true,
						isModified: true,
					},
				];
			}
			return imgs.map((item, idx) => {
				if (idx === requiredImgIdx) {
					return {
						...item,
						fileExtension: getExtension(photo.name)[0],
						src: url,
						originUrl: url,
						isResized: true,
						isModified: true,
					};
				}
				return item;
			});
		}

		case 'EXCHANGE_IMG': {
			const { startIdx, endIdx } = action;

			const updateImgs = imgs.map((item, idx) => {
				if (idx === startIdx) {
					return {
						...imgs[endIdx],
						id: imgs[startIdx].id,
						text: imgs[startIdx].text,
						type: imgs[startIdx].type,
						photoType: imgs[startIdx].photoType,
						isModified: true,
					};
				}
				if (idx === endIdx) {
					return {
						...imgs[startIdx],
						id: imgs[endIdx].id,
						text: imgs[endIdx].text,
						type: imgs[endIdx].type,
						photoType: imgs[endIdx].photoType,
						isModified: true,
					};
				}
				return item;
			});
			return updateImgs;
		}

		case 'SET_TEMP_IMG': {
			return imgs.map((item, idx) => {
				if (idx === action.id) {
					return {
						...item,
						recentBlobUrl: item.src,
					};
				}
				return item;
			});
		}

		case 'SET_IMG_DESC': {
			return imgs.map((item, idx) => {
				if (idx === action.id) {
					return {
						...item,
						description: action.desc,
					};
				}
				return item;
			});
		}

		case 'ROTATE_IMG': {
			return imgs.map((item, idx) => {
				if (idx === action.id) {
					return {
						...item,
						isResized: false,
						isModified: true,
						isValidRatio: action.isValid,
						src: action.data,
						isRotate: true,
					};
				}
				return item;
			});
		}
		case 'MODIFY_IMG': {
			return imgs.map((item, idx) => {
				if (idx === action.id) {
					return {
						...item,
						isModified: true,
						isValidRatio: action.isValid,
						src: action.data,
					};
				}
				return item;
			});
		}

		default: {
			throw Error(`Unknown action: ${action.type}`);
		}
	}
}

export const ImgProvider = ({ children }) => {
	const [imgs, dispatch] = useReducer(imgReducer, initialImgs);
	const imgState = useMemo(() => imgs, [imgs]); // context 최적화
	const uploadedImgs = imgs.filter((item) => item.src !== null);
	return <ImgContext.Provider value={{ imgs: imgState, uploadedImgs, dispatchImg: dispatch }}>{children}</ImgContext.Provider>;
};

export const useImgs = () => useContext(ImgContext);

export default ImgProvider;

